// MACHINERY V2 (Stroje) — V4 below 1280: the compact status list (M2.16b).
//
// Rendered truth: mobile/screenshots/12-v4-phone.png (one column, alarm first)
// and 13-v4-tablet-1024.png (two columns) — both viewed before this file was
// written. Reference impl: mobile/machinery-mobile.jsx MbV4Phone (:412-437) and
// MbV4Tablet (:438-462).
//
// >>> THE 1920 TV WALL IS NOT THIS COMPONENT AND IS NOT TOUCHED BY IT. <<<
// FleetWallView keeps the tile grid, its own header and every line of its
// auto-fit column arithmetic; this is the branch it takes below 1280, and the
// two never both mount.
//
// ============================================================
//  ALARMS-FIRST IS PHONE-ONLY, AND THAT IS THE PACKAGE'S OWN LINE
// ============================================================
// Board 12 puts SBL 500 CNC — the one machine in alarm — at the TOP of the phone
// list, and mobile/README.md §V4 says "alarms sorted first" inside its phone
// clause. Board 13 does NOT: at 1024 the same machine sits in the last cell, in
// the order it arrived. The shipped desktop wall (FleetWallView) does no sorting
// at all — it renders `machines` as handed to it.
//
// So the sort is applied to the PHONE list and to nothing else. Why the line is
// where it is, rather than "sort everywhere, it is better":
//   · a phone shows ~5 rows at once, so an alarm 40 rows down is invisible;
//     a 2-column tablet shows ~12 and a 4-column TV shows ~16, where the red
//     fill does the finding;
//   · a hall TV that REORDERS ITSELF is the worse failure. Operators learn where
//     their machine sits on the wall; a tile that jumps to the top and back on a
//     transient alarm destroys that, and a wall nobody can read at a glance has
//     lost its only job.
// >>> Do NOT lift this sort into FleetWallView. <<< It would reach the desktop
// wall through the shared prop and change a shipped, owner-tested surface.
//
// The sort is STABLE and non-mutating: `machines` is memoised upstream
// (FleetPageData.flat), Array.prototype.sort is spec-stable since ES2019, and
// the input is copied first — so two non-alarm machines never swap places on a
// 60 s telemetry tick.
//
// ONE POLLER. Like every other body, this fetches nothing: `machines`, `states`
// and `telemetry` arrive as props from the single useFleetPageData call in
// FleetPage.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { V2_PHONE, useV2MediaQuery } from "../../useV2MediaQuery";
import { Machine } from "../../../models/models";
import { Notice } from "../FleetNotices";
import {
  MachineryV2CardState,
  MachineryV2Telemetry,
  deriveCardPresentation,
  unconnectedTelemetry,
} from "../cardStates";
import WallMobileHeader from "./WallMobileHeader";
import WallStatusRow from "./WallStatusRow";

/** Both alarm states rank first — a last-known alarm is still an alarm, the
 *  same call wallHeaderCounters makes when it sums `stale-alarm` into the alarm
 *  counter. Read from the STATES map (contract-derived), never from
 *  `alarms.length`. */
const alarmRank = (state: MachineryV2CardState | undefined): number =>
  state === "alarm" || state === "stale-alarm" ? 0 : 1;

export interface WallMobileViewProps {
  machines: Machine[];
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  lastTelemetryOkAt: number;
  isLoading: boolean;
  plant: string;
  plantMore: string | null;
  counts: Record<string, number>;
  onExit: () => void;
}

const WallMobileView: React.FC<WallMobileViewProps> = ({
  machines, states, telemetry, lastTelemetryOkAt, isLoading, plant, plantMore,
  counts, onExit,
}) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: the module's ONE phone predicate — see useV2MediaQuery.ts.
  const isPhone = useV2MediaQuery(V2_PHONE);

  // See the alarms-first block in this file's header for why `isPhone` gates it.
  const rows = useMemo(
    () =>
      isPhone
        ? [...machines].sort(
            (a, b) => alarmRank(states.get(a.id)) - alarmRank(states.get(b.id))
          )
        : machines,
    [machines, states, isPhone]
  );

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", flex: 1, minHeight: 0,
        minWidth: 0, overflow: "hidden", background: "var(--bg)",
      }}
    >
      <WallMobileHeader
        plant={plant}
        plantMore={plantMore}
        counts={counts}
        lastTelemetryOkAt={lastTelemetryOkAt}
        onExit={onExit}
        tablet={!isPhone}
      />

      {/* Scrolls rather than clips, for the reason the desktop grid does: a
          monitoring surface must never silently drop a machine off the bottom. */}
      <main
        style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          padding: isPhone ? "0 14px 14px" : 14,
        }}
      >
        {isLoading ? (
          <Notice text={translate("myMachinery:v2.fleet.loading")} dashed={false} />
        ) : rows.length === 0 ? (
          <Notice text={translate("myMachinery:noMachinesFound")} />
        ) : (
          <div
            style={{
              display: "grid",
              // One column on the phone, two at 768-1279 — board 13's layout,
              // and the widest a 62px row stays readable at.
              gridTemplateColumns: isPhone ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
              gap: isPhone ? 8 : 10,
              alignContent: "start",
              minWidth: 0,
            }}
          >
            {rows.map((m) => {
              const t = telemetry.get(m.id) || unconnectedTelemetry();
              return (
                <WallStatusRow
                  key={m.id}
                  machine={m}
                  presentation={deriveCardPresentation(t)}
                  telemetry={t}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default WallMobileView;
